import 'flag-icons/css/flag-icons.min.css';
import 'primeflex/primeflex.css';

import Checkbox from 'components/form/Checkbox';
import { Form, Formik } from 'formik';
import { Dialog } from 'primereact/dialog';
import { useRef } from 'react';
import { AdminLicenseeModel, CreateLicenseeModel } from 'services/EpdClient';
import styled from 'styled-components';
import { Option, OptionNameCode } from 'types/types';
import * as Yup from 'yup';
import MultiValueLabelWithTooltip from 'components/v2/form/select-data-field/MultiValueLabelWithTooltip';
import { StyledReactSelect } from '../../../../styles/Styles.styled';
import PrimeDropdownField from '../../form/PrimeDropdownField';
import LicenseeThemeIcon from './LicenseeThemeIcon';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { FormikImageUpload } from 'components/v2/images-upload/FormikImageUpload';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { DialogClearStylesButtonsFooter, DialogFooter } from 'components/accounts/DialogClearStyles';
import InputFormField from 'components/v2/form/InputFormField';
import { editAdminSelectStyles } from 'components/form/EditSelectStyles';

const hasAnyVerifier = (value: any[] | undefined | null, anotherVerifierOption: any[] | undefined) => {
  const hasCurrent = value && value.length > 0;
  const hasAnotherOption = anotherVerifierOption && anotherVerifierOption.length > 0;
  return hasCurrent || hasAnotherOption;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Licensee name is required'),
  countries: Yup.array().min(1, 'Required'),
  users: Yup.array().min(1, 'At least one administrator is required'),
  supportEmail: Yup.string().email('Invalid email address').required('Email is required'),
  localHomepage: Yup.string().required('Local homepage is required'),
  licenseeLogo: Yup.mixed()
    .required('Logo is required')
    .test('is-valid-logo', 'Logo is required', (value) => {
      if (!value) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (value instanceof File) return value.size > 0;
      return false;
    }),
  licenseeIndividualVerifiers: Yup.array().when(
    'hasVerifiersList',
    (hasVerifiersList: boolean, schema: Yup.ArraySchema<any>) =>
      hasVerifiersList
        ? schema.test(
            'at-least-one-verifier',
            'At least one individual verifier or certification body is required',
            function (value) {
              const { licenseeCertificationBodyVerifiers } = this.parent;
              return hasAnyVerifier(value, licenseeCertificationBodyVerifiers) ?? false;
            }
          )
        : schema.notRequired()
  ),
  licenseeCertificationBodyVerifiers: Yup.array().when(
    'hasVerifiersList',
    (hasVerifiersList: boolean, schema: Yup.ArraySchema<any>) =>
      hasVerifiersList
        ? schema.test(
            'at-least-one-verifier',
            'At least one individual verifier or certification body is required',
            function (value) {
              const { licenseeIndividualVerifiers } = this.parent;
              return hasAnyVerifier(value, licenseeIndividualVerifiers) ?? false;
            }
          )
        : schema.notRequired()
  ),
});

type TProps = {
  show: boolean;
  onHide: () => void;
  onCreate: (item: CreateLicenseeModel) => void;
  onEdit: (item: AdminLicenseeModel) => void;
  item?: AdminLicenseeModel;
  countriesDs: Option[];
  usersDs: Option[];
  themesDs: OptionNameCode[];
  verifiersDs: Option[];
  certificationBodyDs: Option[];
};

const ManageLicenseeDialog = ({
  show,
  onHide,
  onCreate,
  onEdit,
  item,
  countriesDs,
  usersDs,
  themesDs,
  verifiersDs,
  certificationBodyDs,
}: TProps) => {
  const formRef = useRef<any>();

  const initialValues = item
    ? {
        name: item.name,
        countries: item.countries.map((x) => {
          return { value: x.countryId, label: x.name };
        }),
        users: item.users.map((x) => {
          return { value: x.userId, label: x.name };
        }),
        theme: item.theme,
        supportEmail: item.supportEmail,
        localHomepage: item.localHomepage,
        hasVerifiersList: item.hasVerifiersList,
        licenseeIndividualVerifiers: item.licenseeIndividualVerifiers?.map((x) => {
          return { value: x.id, label: x.name };
        }),
        licenseeCertificationBodyVerifiers: item.licenseeCertificationBodyVerifiers?.map((x) => {
          return { value: x.id, label: x.name };
        }),
        licenseeLogo: item.licenseeLogo,
      }
    : {
        name: '',
        countries: [],
        users: [],
        theme: 'GL',
        supportEmail: '',
        localHomepage: '',
        hasVerifiersList: false,
        licenseeIndividualVerifiers: [],
        licenseeCertificationBodyVerifiers: [],
        logotypeUrl: '',
        licenseeLogo: null,
      };

  const selectedCountryTemplate = (option: any, props: any) => {
    if (option) {
      return (
        <div className="flex align-items-center">
          <LicenseeThemeIcon option={option} />
          <div>{option.name}</div>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const countryOptionTemplate = (option: any) => {
    return (
      <div className="flex align-items-center">
        <LicenseeThemeIcon option={option} />
        <div>{option.name}</div>
      </div>
    );
  };

  return (
    <AddLicenseeStyles
      header={item ? 'Edit Licensee' : 'Add Licensee'}
      visible={show}
      style={{ maxWidth: '33rem', width: '33rem', minWidth: '20rem' }}
      onHide={onHide}
    >
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values: any) => {
          if (item) {
            onEdit(values);
          } else {
            onCreate(values);
          }
        }}
        validationSchema={validationSchema}
      >
        {({ values, isValid, dirty, isSubmitting }) => (
          <Form>
            <Column>
              <FieldGroup>
                <InputFormField
                  required
                  label="Licensee name"
                  name="name"
                  placeholder="Enter Licensee name"
                  errorPositioning="none"
                />
              </FieldGroup>
              <FieldGroup>
                <SmartLabel label="Countries" required={values?.countries.length === 0} />
                <StyledReactSelect
                  name={'countries'}
                  styles={editAdminSelectStyles}
                  className={'w-full'}
                  options={countriesDs}
                  isMulti
                  isClearable
                  placeholder="Pick countries in the Licensee"
                  supressErrors
                />
              </FieldGroup>
              <FieldGroup>
                <SmartLabel label="Licensee Admin" required={values?.users.length === 0} />
                <StyledReactSelect
                  styles={editAdminSelectStyles}
                  name={'users'}
                  options={usersDs}
                  isMulti
                  isClearable
                  className={'w-full'}
                  placeholder="Add Licensee Admin(s)"
                  supressErrors
                />
              </FieldGroup>
              <FieldGroup>
                <InputFormField
                  required
                  label="Support Email"
                  name="supportEmail"
                  placeholder="Enter email address"
                  errorPositioning="none"
                />
              </FieldGroup>
              <FieldGroup>
                <InputFormField
                  required
                  label="Local homepage"
                  name="localHomepage"
                  placeholder="Enter local homepage address"
                  errorPositioning="none"
                />
              </FieldGroup>
              <FieldGroup>
                <SmartLabel
                  label="Licensee logo"
                  required={!values?.licenseeLogo}
                  tooltip="Licensee logo will be used in the EPDs issued by this licensee. It will be displayed on the cover page of the EPD document."
                />
                <FormikImageUpload name="licenseeLogo" hideRequiredError />
              </FieldGroup>

              <FieldGroup>
                <SmartLabel label="UI Skin/theme" required={!initialValues?.theme} />
                <PrimeDropdownField
                  name="theme"
                  options={themesDs}
                  defaultValue={initialValues.theme}
                  optionLabel="name"
                  optionValue="code"
                  placeholder="Select theme"
                  className={'w-full md:w-15rem'}
                  valueTemplate={selectedCountryTemplate}
                  itemTemplate={countryOptionTemplate}
                />
              </FieldGroup>
              <FieldGroup>
                <Checkbox
                  labelStyle={{ fontFamily: 'Inter', fontSize: '0.875rem' }}
                  style={{ margin: '0 0.5rem 0 0' }}
                  label={'Has restricted verifiers list'}
                  name="hasVerifiersList"
                  id="HasVerifiersList"
                />
              </FieldGroup>
              {values.hasVerifiersList && (
                <>
                  <FieldGroup>
                    <SmartLabel
                      label="Individual verifiers"
                      required={
                        !values?.licenseeIndividualVerifiers?.length && !values?.licenseeCertificationBodyVerifiers?.length
                      }
                    />
                    <StyledReactSelect
                      styles={editAdminSelectStyles}
                      name={'licenseeIndividualVerifiers'}
                      className={'w-full'}
                      options={verifiersDs}
                      isMulti
                      isClearable
                      placeholder="Pick individual verifiers"
                      supressErrors
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <SmartLabel
                      label="Accredited certification bodies"
                      required={
                        !values?.licenseeIndividualVerifiers?.length && !values?.licenseeCertificationBodyVerifiers?.length
                      }
                    />
                    <StyledReactSelect
                      styles={editAdminSelectStyles}
                      components={{ MultiValueLabel: MultiValueLabelWithTooltip }}
                      name={'licenseeCertificationBodyVerifiers'}
                      className={'w-full'}
                      options={certificationBodyDs}
                      isMulti
                      isClearable
                      placeholder="Pick certification bodies"
                      supressErrors
                    />
                  </FieldGroup>
                </>
              )}
            </Column>
            <DialogFooter style={{ paddingBottom: '2rem' }}>
              <DialogClearStylesButtonsFooter>
                <SecondaryButton type="button" label="Cancel" onClick={onHide} />
                <PrimaryButton type="submit" label={item ? 'Save' : 'Add'} disabled={!isValid || !dirty || isSubmitting} />
              </DialogClearStylesButtonsFooter>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </AddLicenseeStyles>
  );
};

const AddLicenseeStyles = styled(Dialog)`
  border: none;

  && .p-dialog-header {
    padding: 0;
  }
  && .p-dialog-title {
    margin-top: 16;
  }
`;

const Column = styled.div`
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  box-sizing: border-box;
  margin: 1rem 0;
`;

const FieldGroup = styled.div`
  width: 100%;
  margin: 0rem 0 1rem 0;
`;

export default ManageLicenseeDialog;
